import {UI} from "../../../../../stem-core/ui/UIBase"
import {UserDashboardTab, UserDashboardTabStyle} from "./UserDashboardTab.jsx";
import {UserDashboardSection} from "../components/UserDashboardSection.jsx";
import {Messages} from "../../../../Messages.js";
import {accessDashboardURLFromIFrame, collapseIframe} from "../../../../widget/misc/WidgetUtils";
import {styleRule} from "../../../../../stem-core/decorators/Style";
import {registerStyle} from "../../../../../stem-core/ui/style/Theme";
import {Link} from "../../../../../stem-core/ui/UIPrimitives";
import {PaymentMethod} from "../../../../../client/state/PaymentMethodStore";
import {PaymentMethodInlineMenu} from "../../../../common/inline-menu/PaymentMethodInlineMenu.jsx";
import {PlusIcon} from "../../../../../core/ui/SVGElements.jsx";
import {UserAddress} from "../../../../../client/state/UserAddressStore";
import {AddressInlineMenu} from "../../../../common/inline-menu/AddressInlineMenu.jsx";
import {authService} from "../../../../../client/connection/services/AuthService.js";
import {userService} from "../../../../../client/connection/services/UserService.js";
import {INLINE_USER_DASHBOARD_URLS} from "../../../PanelConstants";
import {Router} from "../../../../../stem-core/ui/Router";
import {iFrameConnection} from "../../../../services/IFrameConnection";
import {IFrameMessages} from "../../../../../blink-sdk/messaging/IFrameMessages";
import {BLINK_PAY_URL} from "../../../../Constants";
import {iFrameUserDataService} from "../../../../services/IFrameUserDataService";
import {MakeMerchantUserResourcesAccessLink} from "../../../../common/MerchantUserResourcesAccessLink";
import {autoredraw} from "../../../../../stem-core/decorators/AutoRedraw";
import {iFrameMerchantService} from "../../../../services/IframeMerchantService";
import {TNR_ALIAS} from "../../../../../blink-sdk/Constants";
import {BlinkInput} from "../../../../ui/Input";
import {UserProfile} from "../../../../../client/state/UserProfileStore";
import {authFormService} from "../../../../services/AuthFormService";
import {Flow} from "../../../flows/Flow.js";
import {LinkButton} from "../../../../../core/ui/LinkButton.jsx";
import {FlowInputIntent} from "../../../flows/steps/BaseFlowStep.jsx";


class UserDashboardAccountTabStyle extends UserDashboardTabStyle {
    @styleRule
    addNew = {
        ">*": {
            display: "inline-block",
        },
        ">:last-child": {
            marginLeft: 8,
        },
    };

    @styleRule
    address = {
        padding: "12px 0",
    };

    @styleRule
    listItem = {
        ":not(:last-child)": {
            borderBottom: "1px solid #E5E5E5",
        }
    };

    @styleRule
    label = {
        fontSize: this.themeProps.FONT_SIZE_NORMAL,
        fontWeight: this.themeProps.FONT_WEIGHT_BOLD,
    };

    @styleRule
    logOutLinkContainer = {
        paddingTop: 12,
        textAlign: "center",
    };
}

@autoredraw(PaymentMethod, UserAddress, UserProfile)
@registerStyle(UserDashboardAccountTabStyle)
export class UserDashboardAccountTab extends UserDashboardTab {
    accountEditMode = false;

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            title: Messages.accountPage,
        }
    }

    async addPaymentMethodClick() {
        await Flow.paymentMethod.prompt({
            inputIntent: FlowInputIntent.ADD_VALUE,
            continueLabel: Messages.addCard,
            inlineSubflow: true,
        });
    }

    async addAddressClick() {
        await Flow.address.prompt({
            inputIntent: FlowInputIntent.ADD_VALUE,
            continueLabel: Messages.addNewAddress,
            inlineSubflow: true,
        });
    }

    renderNameAndEmailSection() {
        const {accountEditMode, styleSheet} = this;

        const canEditProfile = !authService.token.usingMerchantToken || iFrameUserDataService.canEditProfile;

        if (accountEditMode) {
            return <UserDashboardSection
                titleMessage={Messages.personalDetailsTitle}
                secondaryAction={[Messages.cancel, () => {
                    this.accountEditMode = false;
                    this.redraw();
                }]}
                action={[
                    Messages.save,
                    () => this.saveAccountEdit()
                ]}>
                <BlinkInput
                    ref="nameInput"
                    testId="nameInput"
                    label={Messages.name}
                    initialValue={userService.getUserName()}
                    inputAttributes={{placeholder: Messages.name}}
                />
                <BlinkInput
                    ref="emailInput"
                    testId="emailInput"
                    label={Messages.email}
                    initialValue={userService.getUserEmail() || ""}
                    inputAttributes={{placeholder: Messages.email}}
                />
            </UserDashboardSection>
        }

        // Not editing
        return <UserDashboardSection
            titleMessage={Messages.personalDetailsTitle}
            action={[Messages.edit, () => {
                if (!canEditProfile) {
                    iFrameConnection.sendToSDK(IFrameMessages.REDIRECT_TO_URL, {
                        url: BLINK_PAY_URL + "/account", newTab: true
                    });
                    return;
                }
                this.accountEditMode = true;
                this.redraw();
                this.nameInput.focus();
                // TODO it would be nice to focus to end
            }
            ]}>
            <div className={styleSheet.label}>
                {Messages.name}
            </div>
            <div className={styleSheet.textSection}>
                {userService.getUserName()}
            </div>
            <div className={styleSheet.label}>
                {Messages.email}
            </div>
            <div className={styleSheet.textSection}>
                {userService.getUserEmail()}
            </div>
        </UserDashboardSection>
    }

    renderAddressSection() {
        const {styleSheet} = this;
        const extraAddressCount = iFrameUserDataService.getExtraUserAddressCount();

        return <UserDashboardSection titleMessage={Messages.myAddressesTitle}>
            <div>
                {UserAddress.all().map(address =>
                    <AddressInlineMenu
                        className={styleSheet.listItem + styleSheet.address}
                        address={address}
                        handleEdit={() => Flow.address.prompt({
                            selectedValue: address,
                            inputIntent: FlowInputIntent.EDIT_VALUE,
                            noHistory: true,
                        })}
                        handleDelete={!iFrameMerchantService.isMerchant(TNR_ALIAS) && (() => {
                            Flow.address.init({
                                selectedValue: address
                            });

                            Router.changeURL(INLINE_USER_DASHBOARD_URLS.removeAddress);
                        })}
                    />)}
            </div>
            <LinkButton className={styleSheet.addNew} onClick={() => this.addAddressClick()}>
                <PlusIcon size={12.25}/> {Messages.addNew}
            </LinkButton>
            {MakeMerchantUserResourcesAccessLink(extraAddressCount)}
        </UserDashboardSection>
    }

    renderPaymentMethodSection() {
        const {styleSheet} = this;
        const extraPaymentMethodCount = iFrameUserDataService.getExtraPaymentMethodCount();
        const paymentMethods = PaymentMethod.getAvailablePaymentMethods();

        return <UserDashboardSection titleMessage={Messages.paymentMethodsTitle}>
                <div>
                    {paymentMethods.map(paymentMethod =>
                        <PaymentMethodInlineMenu
                            className={styleSheet.listItem}
                            paymentMethod={paymentMethod}
                            handleDelete={(paymentMethod) => {
                                Flow.dashboard.card = paymentMethod;
                                Router.changeURL(INLINE_USER_DASHBOARD_URLS.removeCard);
                            }}
                        />)}
                </div>
                <LinkButton className={styleSheet.addNew} onClick={() => this.addPaymentMethodClick()}>
                    <PlusIcon size={12.25}/> {Messages.addNew}
                </LinkButton>
                {MakeMerchantUserResourcesAccessLink(extraPaymentMethodCount)}
            </UserDashboardSection>
    }

    renderBlinkDashboardLink() {
        return <UserDashboardSection titleMessage={Messages.myDashboard}>
                <div className={this.styleSheet.textSection}>
                    {Messages.dashboardExternalLinkText}
                    <Link
                        href="/"
                        newTab
                        value={Messages.blinkDashboard}
                        onClick={accessDashboardURLFromIFrame}
                    />.
                </div>
            </UserDashboardSection>
    }

    render() {
        const {styleSheet} = this;

        return [
            this.renderNameAndEmailSection(),
            this.renderAddressSection(),
            this.renderPaymentMethodSection(),
            this.renderBlinkDashboardLink(),

            <div className={styleSheet.logOutLinkContainer}>
                <LinkButton label={Messages.logOut} onClick={() => this.handleLogout()} />
            </div>
        ];
    }

    async saveAccountEdit() {
        const newEmail = this.emailInput.getValue().trim();
        const requestedEmailChange = await userService.requestUpdateUserData(this.nameInput.getValue(), newEmail);
        this.accountEditMode = false;
        this.redraw();
        if (requestedEmailChange) {
            authFormService.changeToEmailAddress = newEmail;
            Router.changeURL(INLINE_USER_DASHBOARD_URLS.changeEmailConfirmationCode);
        }
    }

    async handleLogout() {
        collapseIframe();
        await authService.logout();
        // Not specifying a URL here determines the merchant's current page to refresh
        iFrameConnection.sendToSDK(IFrameMessages.REDIRECT_TO_URL, {
            logOut: true,
        });
    }
}
